import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtShort } from "@/lib/scheduling";

export const MessageBubble = ({ m, staffView = false }) => {
  const mine = m.mine;
  const fromMember = m.sender_role === "member";
  return (
    <li className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
      <div className={`max-w-[85%] border px-4 py-3 ${mine ? "border-olive-900 bg-olive-900 text-cream-50" : "border-sage-400/70 bg-cream-100 text-ink-900"}`}>
        <p className={`text-xs ${mine ? "text-cream-50/80" : "text-ink-500"}`}>{mine ? "You" : fromMember ? m.sender_name : `${m.sender_name} · Care Team`} · {fmtShort(m.created_at)}{staffView && m.crisis_flag ? " · crisis words" : ""}</p>
        <p className="mt-1 whitespace-pre-wrap break-words">{m.body}</p>
      </div>
    </li>
  );
};

export const Composer = ({ onSend, pending, placeholder, testPrefix, draft }) => {
  const [text, setText] = useState("");
  useEffect(() => { if (draft?.text) setText(draft.text); }, [draft]);
  const submit = (e) => { e.preventDefault(); if (!text.trim()) return; onSend(text.trim(), () => setText("")); };
  return (
    <form onSubmit={submit} className="mt-4 border-t border-sage-400/60 pt-4" data-testid={`${testPrefix}-composer`}>
      <label htmlFor={`${testPrefix}-input`} className="sr-only">Your message</label>
      <textarea id={`${testPrefix}-input`} className="input min-h-[96px]" maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} data-testid={`${testPrefix}-input`} />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-ink-500">{text.length}/2000</span>
        <button type="submit" className="btn-primary min-h-[44px]" disabled={pending || !text.trim()} data-testid={`${testPrefix}-send`}><Send className="h-4 w-4" aria-hidden="true" /> Send</button>
      </div>
    </form>
  );
};

export default function PortalMessages() {
  const qc = useQueryClient();
  const endRef = useRef(null);
  const [lines, setLines] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["messages", "my"], queryFn: async () => (await api.get("/messages/my")).data, refetchInterval: 30_000 });
  const send = useMutation({
    mutationFn: (body) => api.post("/messages/my", { body }),
    onSuccess: (r) => { if (r.data.crisis_lines) setLines(r.data.crisis_lines); else toast.success("Sent. The care team will reply here."); qc.invalidateQueries({ queryKey: ["messages"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [data?.messages?.length]);
  if (isLoading || !data) return <p className="p-10 text-ink-500" role="status">Loading your messages…</p>;
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid="portal-messages">
      <Link to="/portal" className="link text-sm">My Care Journey</Link>
      <p className="mt-2 text-sm font-semibold text-terracotta-600">Care Team</p>
      <h1 className="mt-1 text-4xl sm:text-5xl">Messages</h1>
      <p className="mt-3 max-w-[60ch] text-ink-500">One running conversation with the people caring for you. We reply here and send you a short email when there is something new. We usually respond {data.response_commitment}. This inbox is not watched around the clock: if you are in crisis, call or text <a href="tel:988" className="link font-semibold">988</a>, or call 911.</p>
      {!data.enabled && <p className="surface mt-6 p-4" data-testid="messages-disabled">Portal messages are not available right now. Please call the care team.</p>}
      {data.enabled && !data.case && <p className="surface mt-6 p-4" data-testid="messages-no-case">Messages open once your Care Intake is received.</p>}
      {lines && (
        <section className="confirm-pop mt-6 border-2 border-terracotta-600 bg-cream-100 p-5" role="alert" data-testid="messages-crisis">
          <p className="font-serif text-xl font-semibold">Thank you for telling us</p>
          <p className="mt-1 text-ink-900">Your message is with the care team now, marked urgent. You do not have to wait for us. Help is available right now:</p>
          <ul className="mt-3 space-y-1">{lines.map((l) => <li key={l.name}><a href={l.href} className="link font-semibold">{l.name}: {l.call}</a>{l.text ? <span className="text-ink-500"> · {l.text}</span> : null}</li>)}</ul>
        </section>
      )}
      {data.enabled && data.case && (
        <section className="mt-6" data-testid="messages-thread">
          <ul className="space-y-3" aria-live="polite" aria-label="Conversation">
            {data.messages.map((m) => <MessageBubble key={m.id} m={m} />)}
            {data.messages.length === 0 && <li className="surface p-5 text-ink-500" data-testid="messages-empty">No messages yet. Write to the care team below — a question about your sessions, something you want your counselor to know ahead of time, or simply how you are doing.</li>}
            <li ref={endRef} aria-hidden="true" />
          </ul>
          <Composer onSend={(body, reset) => send.mutate(body, { onSuccess: reset })} pending={send.isPending} placeholder="Write to the care team…" testPrefix="portal-msg" />
        </section>
      )}
    </div>
  );
}
